import React, { forwardRef, useState, useRef, useEffect } from 'react'
import { CaretDown, Check } from 'phosphor-react'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string
  error?: string
  helperText?: string
  options: SelectOption[]
  placeholder?: string
  wrapperClassname?: string
  onChange?: (value: string) => void
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder = 'Sélectionner...',
      className = '',
      wrapperClassname = 'w-full',
      value,
      onChange,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const selectId = props.id || `select-${Math.random().toString(36).substr(2, 9)}`
    const [isOpen, setIsOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom')
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
    const containerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)

    const selectedOption = options.find(opt => opt.value === value)

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
        }
      }
    }, [isOpen])

    useEffect(() => {
      if (isOpen && triggerRef.current) {
        const calculatePosition = () => {
          if (!triggerRef.current) return

          const rect = triggerRef.current.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const viewportWidth = window.innerWidth
          const spaceBelow = viewportHeight - rect.bottom
          const spaceAbove = rect.top
          const dropdownHeight = 240 // max-h-60 = 240px
          const gap = 4 // mt-1 = 4px
          const minMargin = 8 // Minimum margin from viewport edges

          // Determine best position: above or below
          const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

          if (shouldPositionAbove) {
            setDropdownPosition('top')
            // Position above the trigger
            const availableHeight = spaceAbove - gap - minMargin
            const calculatedTop = rect.top - Math.min(dropdownHeight, availableHeight) - gap
            setDropdownStyle({
              position: 'fixed',
              top: `${Math.max(minMargin, calculatedTop)}px`,
              left: `${Math.max(minMargin, Math.min(rect.left, viewportWidth - rect.width - minMargin))}px`,
              width: `${rect.width}px`,
              maxHeight: `${Math.min(dropdownHeight, availableHeight)}px`,
            })
          } else {
            setDropdownPosition('bottom')
            // Position below the trigger
            const availableHeight = spaceBelow - gap - minMargin
            setDropdownStyle({
              position: 'fixed',
              top: `${rect.bottom + gap}px`,
              left: `${Math.max(minMargin, Math.min(rect.left, viewportWidth - rect.width - minMargin))}px`,
              width: `${rect.width}px`,
              maxHeight: `${Math.min(dropdownHeight, availableHeight)}px`,
            })
          }
        }

        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(calculatePosition, 0)

        // Recalculate on scroll or resize
        const handleScroll = () => {
          calculatePosition()
        }

        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', calculatePosition)

        return () => {
          clearTimeout(timeoutId)
          window.removeEventListener('scroll', handleScroll, true)
          window.removeEventListener('resize', calculatePosition)
        }
      } else {
        setDropdownStyle({})
      }
    }, [isOpen])

    useEffect(() => {
      if (isOpen && dropdownRef.current && focusedIndex !== null) {
        const focusedElement = dropdownRef.current.children[focusedIndex] as HTMLElement
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      }
    }, [focusedIndex, isOpen])

    const handleSelect = (optionValue: string) => {
      if (onChange) {
        onChange(optionValue)
      }
      setIsOpen(false)
      setFocusedIndex(null)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (!isOpen) {
            setIsOpen(true)
          } else if (focusedIndex !== null) {
            handleSelect(options[focusedIndex].value)
          }
          break
        case 'Escape':
          setIsOpen(false)
          setFocusedIndex(null)
          break
        case 'ArrowDown':
          e.preventDefault()
          if (!isOpen) {
            setIsOpen(true)
          } else {
            setFocusedIndex(prev =>
              prev === null ? 0 : Math.min(prev + 1, options.length - 1)
            )
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (isOpen) {
            setFocusedIndex(prev =>
              prev === null ? options.length - 1 : Math.max(prev - 1, 0)
            )
          }
          break
      }
    }

    const baseStyles =
      'w-full px-4 py-2.5 text-base border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 cursor-pointer'

    const stateStyles = error
      ? 'border-[var(--color-error)] focus:border-[var(--color-error)] focus:ring-[var(--color-error)]'
      : 'border-[var(--color-border)] focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)]'

    return (
      <div className={wrapperClassname} ref={containerRef}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
          >
            {label}
            {required && <span className="text-[var(--color-error)] ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <div
            ref={triggerRef}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={handleKeyDown}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={`${baseStyles} ${stateStyles} ${className} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${isOpen ? 'ring-2 ring-[var(--color-primary)] ring-offset-1' : ''}`}
          >
            <div className="flex items-center justify-between">
              <span
                className={`${
                  selectedOption
                    ? 'text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-muted)]'
                }`}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <CaretDown
                className={`w-5 h-5 text-[var(--color-text-muted)] transition-transform duration-200 ${
                  isOpen ? 'transform rotate-180' : ''
                }`}
                weight="regular"
              />
            </div>
          </div>

          {/* Hidden select for form submission */}
          <select
            ref={ref}
            id={selectId}
            value={value || ''}
            onChange={e => onChange && onChange(e.target.value)}
            disabled={disabled}
            required={required}
            className="sr-only"
            {...props}
          >
            <option value="">{placeholder}</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {isOpen && (
            <div
              ref={dropdownRef}
              role="listbox"
              style={dropdownStyle}
              className={`z-[9999] bg-white border border-[var(--color-border)] rounded-lg shadow-lg max-h-60 overflow-auto ${
                dropdownPosition === 'top' ? 'mb-1' : 'mt-1'
              }`}
            >
              {options.map((option, index) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  className={`px-4 py-2.5 cursor-pointer transition-colors duration-150 flex items-center justify-between ${
                    option.value === value
                      ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'text-[var(--color-text-primary)] hover:bg-[var(--color-gray-100)]'
                  } ${
                    focusedIndex === index
                      ? 'bg-[var(--color-gray-100)]'
                      : ''
                  }`}
                >
                  <span>{option.label}</span>
                  {option.value === value && (
                    <Check className="w-5 h-5 text-[var(--color-primary)]" weight="bold" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-[var(--color-error)]" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-[var(--color-text-muted)]">{helperText}</p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

